import {useEffect} from 'react';

import {FlowchartNode} from '../processFlowchartConfigEditorNodeTypes';
import {ApplyFlowchartChangesFn} from './FlowchartStateTypes';

interface UsePlaceholderNodeSidepanelParams {
  placeholderNodeIdBeingAdded: string | null;
  setPlaceholderNodeBeingAdded: (value: string | null) => void;
  interactiveNodes: Array<FlowchartNode>;
  applyFlowchartChanges: ApplyFlowchartChangesFn;
  openSidepanelForNode: (nodeId: string) => void;
}

/**
 * After a placeholder node is added and appears in `interactiveNodes`, select
 * it and open its sidepanel. Waits for the node to actually be mounted before
 * opening the sidepanel so the ref lookup succeeds.
 */
export function usePlaceholderNodeSidepanel({
  placeholderNodeIdBeingAdded,
  setPlaceholderNodeBeingAdded,
  interactiveNodes,
  applyFlowchartChanges,
  openSidepanelForNode,
}: UsePlaceholderNodeSidepanelParams): void {
  useEffect(() => {
    if (placeholderNodeIdBeingAdded == null) {
      return;
    }
    const nodeExists = interactiveNodes.some((node) => node.id === placeholderNodeIdBeingAdded);
    if (!nodeExists) {
      return;
    }

    applyFlowchartChanges(() => ({
      nodeChanges: [
        {
          type: 'select',
          id: placeholderNodeIdBeingAdded,
          selected: true,
        },
      ],
    }));
    // Open the sidepanel after a brief delay to ensure the getNodeById ref is updated.
    setTimeout(() => {
      openSidepanelForNode(placeholderNodeIdBeingAdded);
    }, 0);
    setPlaceholderNodeBeingAdded(null);
  }, [
    placeholderNodeIdBeingAdded,
    openSidepanelForNode,
    setPlaceholderNodeBeingAdded,
    applyFlowchartChanges,
    interactiveNodes,
  ]);
}
